import React, { useEffect, useState } from "react";
import { Btn, Eyebrow } from "../../components/Primitives";
import { api } from "../../lib/api";
import logger from "../../lib/logger";

const SLOT_LABELS = {
    afternoon: "Afternoon (12-3pm)",
    early_eve: "Early evening (5-8pm)",
    evening: "Evening (7-10pm)",
};
const REASON_LABEL = {
    pair_fit: "Picked for your archetype pairing",
    city: "Local to your city",
    any: "Suggested",
};

function fmtWindow(w) {
    const d = new Date(`${w.date}T00:00:00`);
    const label = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    return `${label} · ${SLOT_LABELS[w.slot]}`;
}

export default function VenuePicker({ user, matchId, overlap, onProposed, onCancel }) {
    const [chosenWindow, setChosenWindow] = useState(overlap[0]);
    const [suggestions, setSuggestions] = useState([]);
    const [chosenVenueId, setChosenVenueId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const wk = `${chosenWindow.date}|${chosenWindow.slot}`;
        api.dating.venueSuggestions(matchId, wk, user.session_id)
            .then(r => { if (!cancelled) { setSuggestions(r.suggestions || []); setChosenVenueId((r.suggestions || [])[0]?.id || null); } })
            .catch(e => { logger.error("venue suggestions failed", e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [chosenWindow, matchId, user.session_id]);

    const propose = async () => {
        if (!chosenWindow || !chosenVenueId) return;
        setSubmitting(true);
        try {
            const r = await api.dating.proposeDate(matchId, user.session_id, chosenWindow, chosenVenueId);
            onProposed(r);
        } catch (e) {
            logger.error("propose failed", e);
            alert(e?.response?.data?.detail || "Could not propose date.");
        }
        setSubmitting(false);
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="venue-picker">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="venue-back-btn" onClick={onCancel} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">← Back</button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Step 2 of 3</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>Overlap found</Eyebrow>
                <h1 className="mt-2 font-display text-3xl text-burgundy leading-[1.15]">
                    Pick a <span className="italic">time</span> and a <span className="italic">place.</span>
                </h1>
                <p className="mt-2 text-sm text-ink-muted leading-snug">
                    You both said yes to these windows. Choose one — then a venue we think fits the two of you.
                </p>
            </div>

            <div className="flex-1 px-6 py-3 overflow-y-auto space-y-5">
                <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-2">When</div>
                    <div className="space-y-2">
                        {overlap.map(w => {
                            const k = `${w.date}|${w.slot}`;
                            const active = chosenWindow && `${chosenWindow.date}|${chosenWindow.slot}` === k;
                            return (
                                <button
                                    key={k}
                                    data-testid={`overlap-${k}`}
                                    onClick={() => setChosenWindow(w)}
                                    className={`w-full text-left px-4 py-3 border transition-colors ${
                                        active ? "border-burgundy bg-burgundy text-cream" : "border-burgundy/15 bg-white text-burgundy hover:border-burgundy/40"
                                    }`}
                                >
                                    <div className="font-display text-lg">{fmtWindow(w)}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-2">Where</div>
                    {loading ? (
                        <div className="text-ink-muted text-sm py-6 text-center">Finding venues…</div>
                    ) : suggestions.length === 0 ? (
                        <div className="text-ink-muted text-sm py-6 text-center">No partner venues match this city yet.</div>
                    ) : (
                        <div className="space-y-2">
                            {suggestions.map(v => {
                                const active = chosenVenueId === v.id;
                                return (
                                    <button
                                        key={v.id}
                                        data-testid={`venue-${v.id}`}
                                        onClick={() => setChosenVenueId(v.id)}
                                        className={`w-full text-left p-4 border transition-colors ${
                                            active ? "border-burgundy bg-burgundy text-cream" : "border-burgundy/15 bg-white text-ink hover:border-burgundy/40"
                                        }`}
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="font-display text-lg">{v.name}</span>
                                            <span className={`text-[9px] tracking-[0.28em] uppercase ${active ? "text-cream/80" : "text-gold-deep"}`}>
                                                {REASON_LABEL[v.match_reason] || ""}
                                            </span>
                                        </div>
                                        <div className={`text-xs ${active ? "text-cream/80" : "text-ink-muted"}`}>{v.neighborhood} · {v.city}</div>
                                        <p className={`mt-1 text-xs leading-snug ${active ? "text-cream/90" : "text-ink"}`}>{v.one_line_pitch}</p>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="px-6 pb-8 pt-3 border-t border-burgundy/10 bg-cream">
                <Btn data-testid="propose-btn" variant="primary" onClick={propose} disabled={!chosenVenueId || submitting}>
                    {submitting ? "Sending…" : "Propose this date"}
                </Btn>
            </div>
        </div>
    );
}
